import { CreatableMultiSelect } from '@/components/CreatableMultiSelect'
import { SectionCard } from '@/components/SectionCard/SectionCard'
import { PageLayout } from '@/components/templates/PageLayout/PageLayout'
import { EditorWrapper } from '@/features/Templates/components/EditorWrapper'
import { templateTableRoute } from '@/features/Templates/page/TemplateTable'
import {
  useGetTags,
  useGetTemplateById,
  usePostTemplate,
} from '@/generated/http-clients/v3ApiComponents'
import type { NewTemplate } from '@/generated/http-clients/v3ApiSchemas'
import { toaster } from '@/hooks/useToast/toastUtils'
import { templatesRootRoute } from '@/router/rootRoutes'
import { NBSP } from '@/utils'
import { Button, Field, Input, Text, VStack } from '@chakra-ui/react'
import { createRoute, useNavigate } from '@tanstack/react-router'
import type React from 'react'
import { useEffect, useState } from 'react'
import { z } from 'zod'

// Regex for SSH Git URLs (e.g., git@github.com:myproject/something.git)
const sshGitUrlRegex = /^[\w-]+@[\w.-]+:[\w./-]+\.git$/

export const templateCreateRoute = createRoute({
  getParentRoute: () => templatesRootRoute,
  path: 'create',
  component: () => <TemplateCreate />,
})
const formSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
  chart_source: z.object({
    chart_path: z.string().min(1, 'Chart Path is required'),
    chart_repo_url: z
      .string()
      .min(1, 'Repository URL is required.')
      .refine(
        (value) => /^https?:\/\/.+/.test(value) || sshGitUrlRegex.test(value),
        { message: 'Repository URL must be a valid HTTP(S) or SSH Git URL.' },
      ),
    chart_target_revision: z.string().min(1, 'Target Revision is required'),
  }),
  tags: z.array(z.string()).optional(),
  yaml: z.string().min(1, 'YAML content is required'),
})

// Define the type for the search parameters
const searchParamsSchema = z.object({
  templateId: z.string().optional(),
})

export function TemplateCreate() {
  const searchParams = templateCreateRoute.useSearch()
  const { templateId } = searchParamsSchema.parse(searchParams)

  const { data: templateData } = useGetTemplateById(
    { pathParams: { templateId: templateId || '' } },
    { enabled: !!templateId }, // Only fetch if templateId is present
  )

  // If templateId is provided, modify the name and description for the new template
  const clonedTemplateData = templateData ? { ...templateData } : null
  if (clonedTemplateData && !clonedTemplateData.name.endsWith('(copy)')) {
    clonedTemplateData.description = `Copy of template ${templateId} (${clonedTemplateData.name})`
    clonedTemplateData.name = `${clonedTemplateData.name} (copy)`
  }

  const [formValues, setFormValues] = useState<NewTemplate>(
    templateData || {
      name: '',
      description: '',
      tags: [],
      chart_source: {
        chart_path: '',
        chart_repo_url: '',
        chart_target_revision: '',
      },
      yaml: '',
    },
  )

  useEffect(() => {
    if (templateData) {
      setFormValues(templateData)
    }
  }, [templateData])

  const [errors, setErrors] = useState<Record<string, string>>({})
  const createTemplate = usePostTemplate()
  const tags = useGetTags({})
  const navigate = useNavigate()

  function submitCreateTemplate() {
    // Validate form values
    const validationResult = formSchema.safeParse(formValues)

    if (!validationResult.success) {
      const validationErrors = validationResult.error.errors.reduce<
        Record<string, string>
      >((acc, err) => {
        if (err.path.length) {
          let key = err.path.join('.')
          // Remove "chart_source." prefix for its children, this makes it a lot
          // easier to map the errors.
          if (key.startsWith('chart_source.')) {
            key = key.replace('chart_source.', '')
          }
          acc[key] = err.message
        }
        return acc
      }, {})

      setErrors(validationErrors)

      return
    }

    setErrors({})

    createTemplate
      .mutateAsync({
        body: formValues,
      })
      .then(() => {
        toaster.create({
          title: 'Request to create template was sent successfully.',
          type: 'success',
          duration: 2000,
        })
        return navigate({ to: templateTableRoute.to })
      })
      .catch((error) => {
        console.error('error creating template', error)
        toaster.create({
          title: 'Failed to create template.',
          type: 'error',
          duration: 5000,
        })
      })
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target

    setFormValues((prevValues) => {
      if (name in prevValues.chart_source) {
        return {
          ...prevValues,
          chart_source: {
            ...prevValues.chart_source,
            [name]: value,
          },
        }
      }
      return {
        ...prevValues,
        [name]: value,
      }
    })
  }

  const handleChangeYaml = (yaml: string | undefined) => {
    if (yaml) {
      setFormValues((prevValues) => ({
        ...prevValues,
        yaml,
      }))
    }
  }

  return (
    <PageLayout
      title="Create Template"
      action={
        <Button variant="solid" onClick={submitCreateTemplate}>
          Submit template
        </Button>
      }
    >
      <SectionCard title="Add template information">
        <VStack alignItems="start" gap={6}>
          <Field.Root required invalid={Boolean(errors.name)}>
            <Field.Label>Name</Field.Label>
            <Input
              id="name"
              name="name"
              placeholder="Enter a name for your template"
              value={formValues.name}
              onChange={handleInputChange}
            />
            {errors.name && <Field.ErrorText>{errors.name}</Field.ErrorText>}
          </Field.Root>

          <Field.Root>
            <Field.Label display="inline-flex">
              Description{NBSP}
              <Text fontWeight={400} color="text.text-subtle">
                (Optional)
              </Text>
            </Field.Label>
            <Input
              id="description"
              name="description"
              placeholder="Enter a description for your template"
              value={formValues.description}
              onChange={handleInputChange}
            />
            {errors.description ? (
              <Field.ErrorText>{errors.description}</Field.ErrorText>
            ) : null}
          </Field.Root>

          <Field.Root required invalid={Boolean(errors.chart_repo_url)}>
            <Field.Label>Repository URL</Field.Label>
            <Input
              id="chart_repo_url"
              name="chart_repo_url"
              placeholder="Enter the chart repository"
              value={formValues.chart_source.chart_repo_url}
              onChange={handleInputChange}
            />
            {errors.chart_repo_url && (
              <Field.ErrorText>{errors.chart_repo_url}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root required invalid={Boolean(errors.chart_target_revision)}>
            <Field.Label>Target Revision</Field.Label>
            <Input
              id="chart_target_revision"
              name="chart_target_revision"
              placeholder="Enter the chart target revision"
              value={formValues.chart_source.chart_target_revision}
              onChange={handleInputChange}
            />
            {errors.chart_target_revision && (
              <Field.ErrorText>{errors.chart_target_revision}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root required invalid={Boolean(errors.chart_path)}>
            <Field.Label>Chart Path</Field.Label>
            <Input
              id="chart_path"
              name="chart_path"
              placeholder="Enter the chart path"
              value={formValues.chart_source.chart_path}
              onChange={handleInputChange}
            />
            {errors.chart_path && (
              <Field.ErrorText>{errors.chart_path}</Field.ErrorText>
            )}
          </Field.Root>
          <Field.Root>
            <Field.Label display="inline-flex">
              Tags{NBSP}
              <Text fontWeight={400} color="text.text-subtle">
                (Optional)
              </Text>
            </Field.Label>
            <CreatableMultiSelect
              value={formValues.tags || []}
              onChange={(newTags) =>
                setFormValues((prev) => ({
                  ...prev,
                  tags: newTags,
                }))
              }
              options={tags.data?.tags || []}
              placeholder="Create or select tags"
              isClearable
            />
            <Field.HelperText>
              Select existing tags or create new ones. Available tags:{NBSP}
              {(tags.data?.tags || []).join(', ')}
            </Field.HelperText>
          </Field.Root>
        </VStack>
      </SectionCard>
      <SectionCard title="Add template YAML">
        <Field.Root invalid={Boolean(errors.yaml)}>
          {errors.yaml && (
            <Field.ErrorText mb={2}>{errors.yaml}</Field.ErrorText>
          )}
          <EditorWrapper
            onChange={handleChangeYaml}
            yamlContent={formValues.yaml}
          />
        </Field.Root>
      </SectionCard>
    </PageLayout>
  )
}
